import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { LogOut, LayoutDashboard, Users, ClipboardCheck, CalendarCheck2, FileSearch, FileBarChart2, Download, Settings, Menu, ArrowLeft } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Resumen", icon: LayoutDashboard, end: true },
  { to: "/admin/participantes", label: "Participantes", icon: Users },
  { to: "/admin/diagnostico", label: "Diagnóstico", icon: ClipboardCheck },
  { to: "/admin/actividades", label: "Actividades", icon: CalendarCheck2 },
  { to: "/admin/revisiones", label: "Revisiones (IAC)", icon: FileSearch },
  { to: "/admin/informe-brechas", label: "Informe de Brechas", icon: FileBarChart2 },
  { to: "/admin/exportaciones", label: "Exportaciones", icon: Download },
  { to: "/admin/ajustes", label: "Ajustes", icon: Settings },
];

function SidebarContent({ admin, onNavigate, onLogout, navigate }) {
  // "Volver al inicio" lleva a la página principal PÚBLICA de la plataforma
  // (landing "/"), no al resumen interno del panel admin. No cierra sesión:
  // la cookie de sesión de administrador permanece intacta.
  const goHome = () => { navigate("/"); onNavigate(); };
  return (
    <div className="h-full flex flex-col">
      <div className="px-5 py-5 border-b border-white/10">
        <button type="button" onClick={goHome} data-testid="admin-sidebar-logo" className="bg-white rounded-xl px-2.5 py-1.5 inline-block mb-2.5 hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50" aria-label="Volver al inicio de la plataforma">
          <img src="/brand/inside-logo.png" alt="INSIDE Training & Learning" className="h-6 w-auto" />
        </button>
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2.5">ANID · Piloto 48 horas</p>
        <button type="button" onClick={goHome} data-testid="admin-back-home-link" className="flex items-center gap-1.5 text-[11px] font-bold text-slate-300 hover:text-white transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio
        </button>
      </div>
      <nav className="flex-1 py-4 space-y-1 px-3 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-bold transition-colors border-l-2 ${isActive ? "bg-white/10 text-white border-brand-coral" : "text-slate-400 border-transparent hover:text-white hover:bg-white/5"}`}
          >
            <item.icon className="w-4 h-4" /> {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <p className="text-[10px] text-slate-400 mb-2 truncate" data-testid="admin-sidebar-email">{admin?.email}</p>
        <button data-testid="admin-logout-btn" onClick={onLogout} className="w-full flex items-center gap-2 text-xs font-bold text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg transition-colors">
          <LogOut className="w-4 h-4" /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)] lg:flex">
      {/* Sidebar fija en desktop */}
      <aside className="hidden lg:flex w-60 bg-brand-navy text-white flex-col shrink-0" data-testid="admin-sidebar">
        <SidebarContent admin={admin} onNavigate={() => {}} onLogout={handleLogout} navigate={navigate} />
      </aside>

      {/* Topbar + drawer en mobile/tablet */}
      <div className="lg:hidden sticky top-0 z-40 bg-brand-navy text-white flex items-center justify-between px-4 py-3 border-b border-white/10">
        <button type="button" onClick={() => navigate("/")} data-testid="admin-mobile-logo-home" className="flex items-center gap-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg" aria-label="Volver al inicio de la plataforma">
          <div className="bg-white rounded-lg px-2 py-1">
            <img src="/brand/inside-logo.png" alt="INSIDE" className="h-5 w-auto" />
          </div>
          <span className="text-xs font-bold text-slate-300">Admin</span>
        </button>
        <button
          data-testid="admin-mobile-menu-btn"
          onClick={() => setMobileNavOpen(true)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="bg-brand-navy text-white p-0 border-0 w-72">
          <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
          <SidebarContent admin={admin} onNavigate={() => setMobileNavOpen(false)} onLogout={handleLogout} navigate={navigate} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
